// ============================================================
// A TOKEN'S LOOKS ON THE MAP (plan body-map M1; the user's stacking rulings,
// 2026-10-09; the Auras design page, tools/plate-art/canvas/map/auras.html).
// The map's figures carry the plate's aura groups, each in slots: the shadow,
// the base ring, ground bands nesting outward (the field's hex rows, then the
// rune ring, then the conduit pool), pulses starting outside the last band,
// the stalk, layers of the head. All of them on; past three, quieter and
// slower until the token is looked at. This reads WHICH looks a body shows
// and WHERE their bands lie; the drawing is components/display/TokenLooks.
//
// Radii are in ring radii (the figure's base ring is 1), so the drawing
// scales them by the token's own ring. Pure. Zero React dependency.
// ============================================================

import type { Target } from '../../types';
import { figureFor } from '../plate/figureFor';
import { traitLooks, type LookSheet, type TraitLooks } from '../plate/traitLooks';
import { WRONG_PARTS } from '../plate/natures';
import { between, rngFor } from './rng';

export type MapLook =
  | 'swift'
  | 'daemonic'
  | 'night'
  | 'field'
  | 'psyker'
  | 'conduit'
  | 'regen'
  | 'toxic'
  | 'undying'
  | 'fear'
  | 'beyond'
  | 'natarm'
  | 'frenzied'
  | 'warpinst';

/**
 * Which looks a body shows, read off the plate's own reading of it. Warp Instability draws nothing at rest - its look is
 * the backtrack when struck (the beat's, engine/display/beats) - but counts among the looks, as on the design page. The
 * plate-only marks (Sturdy, Black Carapace, Deadly Natural Weapons, Strange Physiology, Blind) are not drawn on the map
 * at all (user, 2026-10-09).
 */
const SHOWS: Record<MapLook, (l: TraitLooks) => boolean> = {
  swift: (l) => l.swift,
  daemonic: (l) => l.daemon > 0,
  night: (l) => l.nightmares,
  field: (l) => l.field > 0,
  psyker: (l) => l.psy > 0,
  conduit: (l) => l.conduit,
  regen: (l) => l.regen > 0,
  toxic: (l) => l.toxic > 0,
  undying: (l) => l.undying,
  fear: (l) => l.fear > 0,
  beyond: (l) => l.beyond,
  natarm: (l) => l.natArmour > 0,
  frenzied: (l) => l.frenzied,
  warpinst: (l) => l.warpInst,
};

/** The drawing order: the order the slots are handed out in (the design page's ORDER). */
const ORDER = Object.keys(SHOWS) as MapLook[];

/** The looks a body shows on the map, in the drawing order. */
export const mapLooks = (l: TraitLooks): MapLook[] => ORDER.filter((k) => SHOWS[k](l));

/**
 * A placed body's looks on the map: its nature, read off its projection and its sheet as the plate reads them. Dead or
 * alive - a killing hit on a Warp Instability body still backtracks; the MARKER stops drawing a dead figure's looks.
 */
export function bodyMapLooks(target: Target, sheet: LookSheet | null): MapLook[] {
  // The same array while the (memoised) projection and the sheet are: a memo keyed on the looks then holds.
  const kept = readLooks.get(target);
  if (kept?.sheet === sheet) return kept.looks;
  const f = figureFor(target, sheet);
  const looks = mapLooks(traitLooks(target, sheet, f.kind === 'person' ? f.figure : 'human'));
  readLooks.set(target, { sheet, looks });
  return looks;
}
const readLooks = new WeakMap<Target, { sheet: LookSheet | null; looks: MapLook[] }>();

/** The design's base ring (m): every distance below is measured in it. */
const R = 0.46;
/** A distance in the design's metres, in base rings (its ring is 0.46 m): the looks' one scale. */
export const rings = (m: number) => m / R;

/** The looks that claim a ground band, and how each takes the next free radius (`ring`) - the design page's numbers. */
const BANDS: Partial<Record<MapLook, (ring: number) => { radii: number[]; next: number }>> = {
  field: (ring) => {
    const r0 = ring + rings(0.07);
    return { radii: [r0, r0 + rings(0.33)], next: r0 + rings(0.5) };
  },
  psyker: (ring) => {
    const r = Math.max(rings(0.95), ring + rings(0.1));
    return { radii: [r], next: r + rings(0.15) };
  },
  conduit: (ring) => {
    const r = Math.max(1.9, ring + rings(0.1));
    return { radii: [r], next: r };
  },
};

export interface LookSlots {
  /** Each banded look's radii, nesting outward in the drawing order. */
  bands: Partial<Record<'field' | 'psyker' | 'conduit', number[]>>;
  /** Where a pulse starts (Toxic out, Undying in): outside the last band, else at the ring. */
  pulse0: number;
}

/** Where each look's ground band lies: the first just outside the base ring, each next outside the last. */
export function lookSlots(keys: readonly MapLook[]): LookSlots {
  const bands: LookSlots['bands'] = {};
  let ring = 1.45;
  let pulse0 = 1;
  for (const k of keys) {
    const band = BANDS[k]?.(ring);
    if (!band) continue;
    bands[k as keyof LookSlots['bands']] = band.radii;
    ring = band.next;
    pulse0 = ring;
  }
  return { bands, pulse0 };
}

/**
 * How loud the looks are: all at full up to three; past three, dimmer (3/n, never under 0.4) and slower (0.6x) - until
 * the token is looked at (hovered, the actor or the target), when all are at full again.
 */
export function lookQuiet(n: number, focused: boolean): { opacity: number; pace: number } {
  if (focused || n <= 3) return { opacity: 1, pace: 1 };
  return { opacity: Math.max(0.4, Number((3 / n).toFixed(3))), pace: 0.6 };
}

/** The psyker's 18 rune strokes round its ring: three points each, a little off the ring, in ring units (seeded). */
function runeStrokesOf(seed: string): [number, number][][] {
  const rng = rngFor(seed, 'runes');
  return Array.from({ length: 18 }, (_, i) => {
    const a = (i / 18) * Math.PI * 2;
    return [0, 1, 2].map((j): [number, number] => {
      const r = 1 + between(rng, -0.085, 0.085);
      return [round(r * Math.cos(a + j * 0.05)), round(r * Math.sin(a + j * 0.05))];
    });
  });
}

/** The psyker's lightning from its head: a jagged arc out to one side (design pixels; the drawing scales it). */
function boltPathOf(seed: string): string {
  const rng = rngFor(seed, 'bolt');
  const a = between(rng, 0, Math.PI * 2);
  const [ex, ey] = [Math.cos(a) * 26, Math.sin(a) * 14];
  const legs = Array.from({ length: 6 }, (_, i) => {
    const k = (i + 1) / 6;
    const jag = i < 5 ? 9 : 0;
    return `L${round(ex * k + between(rng, -0.5, 0.5) * jag)} ${round(ey * k + between(rng, -0.5, 0.5) * jag)}`;
  });
  return `M0 0 ${legs.join(' ')}`;
}

/** The sky behind From Beyond's windows: stars inside a unit disc, a few warm (seeded; the windows clip it). */
function voidStarsOf(seed: string): { x: number; y: number; r: number; warm: boolean }[] {
  const rng = rngFor(seed, 'stars');
  return Array.from({ length: 60 }, (_, i) => {
    const [d, a] = [Math.sqrt(rng()), between(rng, 0, Math.PI * 2)];
    return {
      x: round(d * Math.cos(a)),
      y: round(d * Math.sin(a)),
      r: round(between(rng, 0.3, 1.4)),
      warm: i % 9 === 0,
    };
  });
}

/** The nightmare's shadow: its head and two arms each swinging on its own, as far as the plate's (degrees, seconds; seeded). */
function nightLimbsOf(seed: string): { swing: number; dur: number; delay: number }[] {
  const rng = rngFor(seed, 'night');
  return [0, 1, 2].map((i) => ({
    swing: Math.round(between(rng, -WRONG_PARTS[i].max, WRONG_PARTS[i].max)),
    dur: Number(between(rng, 2, 4.5).toFixed(2)),
    delay: Number(between(rng, 0, 2).toFixed(2)),
  }));
}

const round = (n: number) => Number(n.toFixed(3));

/** How many tokens' shapes are kept: more than any table shows at once, so a long session does not grow without end. */
const KEPT = 256;

/**
 * A seeded shape, worked out once per token: a marker redraws on every camera move, and its runes, stars, limbs and
 * bolt depend on nothing but its id. The oldest is forgotten past KEPT (a Map keeps insertion order).
 */
function once<T>(make: (seed: string) => T): (seed: string) => T {
  const made = new Map<string, T>();
  return (seed) => {
    if (!made.has(seed)) {
      if (made.size >= KEPT) made.delete(made.keys().next().value!);
      made.set(seed, make(seed));
    }
    return made.get(seed)!;
  };
}

/** The seeded shapes, once per token (see each maker above). */
export const runeStrokes = once(runeStrokesOf);
export const boltPath = once(boltPathOf);
export const voidStars = once(voidStarsOf);
export const nightLimbs = once(nightLimbsOf);
